import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { getProductById } from "@/data/products";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function ProductDetail() {
  const { id } = useParams();
  const product = getProductById(id);

  if (!product) {
    return (
      <div data-testid="product-not-found" className="py-32 text-center">
        <h1 className="font-serif text-4xl">Product not found</h1>
        <Link to="/products" className="mt-6 inline-flex items-center gap-2 text-[#3FD97A]">
          <ArrowLeft size={16} /> Back to products
        </Link>
      </div>
    );
  }

  return (
    <div data-testid={`product-detail-${id}`} className="py-16 md:py-20">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        {/* Breadcrumb */}
        <Link
          to="/products"
          data-testid="back-to-products"
          className="inline-flex items-center gap-2 text-foreground/70 hover:text-[#3FD97A] font-mono text-[11px] uppercase tracking-[0.22em]"
        >
          <ArrowLeft size={14} /> Back to catalogue
        </Link>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image */}
          <div className="aspect-[4/3] bg-[#141212] overflow-hidden">
            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
          </div>

          {/* Info */}
          <div>
            <div className="overline">{product.sku} • {product.category}</div>
            <h1 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight">{product.name}</h1>
            <p className="mt-6 text-foreground/75 text-lg leading-relaxed">
              {product.description}
            </p>

            <div className="mt-10">
              <h3 className="overline">Minimum Order Quantity</h3>
              <p className="mt-3 font-serif text-3xl">{product.moq}</p>
            </div>

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to="/rfq"
                data-testid="product-rfq-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#3FD97A] hover:bg-[#4FE98A] text-[#0A0909] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover-shift font-semibold"
              >
                Request quote <ArrowUpRight size={16} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border border-border hover:border-[#3FD97A] px-7 py-4 font-mono text-[11px] uppercase tracking-[0.22em] hover-shift"
              >
                Ask a question
              </Link>
            </div>
          </div>
        </div>

        {/* Specifications */}
        <div className="mt-20">
          <h2 className="font-serif text-4xl tracking-tight mb-10">Technical Specifications</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Grades */}
            <div className="border border-border p-8 bg-[#0A0909]">
              <h3 className="overline mb-6">Available Grades</h3>
              <ul className="space-y-3">
                {product.grades.map((grade) => (
                  <li key={grade} className="flex items-center gap-3">
                    <Check size={16} className="text-[#3FD97A]" />
                    <span className="text-foreground/90">{grade}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Packaging */}
            <div className="border border-border p-8 bg-[#0A0909]">
              <h3 className="overline mb-6">Packaging Options</h3>
              <ul className="space-y-3">
                {product.packaging.map((pack) => (
                  <li key={pack} className="flex items-center gap-3">
                    <Check size={16} className="text-[#3FD97A]" />
                    <span className="text-foreground/90">{pack}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Granulation Table */}
          <div className="mt-10 border border-border">
            <div className="p-6 bg-[#0A0909] border-b border-border">
              <h3 className="overline">Granulation / Variant Options</h3>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">Option</TableHead>
                  <TableHead className="font-mono text-[10px] uppercase tracking-[0.2em]">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {product.granulation.map((gran, idx) => (
                  <TableRow key={idx}>
                    <TableCell className="font-mono text-sm">{idx + 1}</TableCell>
                    <TableCell>{gran}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>

        {/* Quality Note */}
        <div className="mt-20 border-l-2 border-[#3FD97A] pl-8 py-6 bg-[#0A0909]">
          <h3 className="font-serif text-2xl mb-4">Quality Assurance</h3>
          <p className="text-foreground/75 leading-relaxed">
            All products are lab-tested and certified. Certificates of Analysis (COA) from independent third-party labs (SGS, Bureau Veritas, or Intertek) are available upon request. We maintain full traceability from farm to port.
          </p>
        </div>
      </div>
    </div>
  );
}
